/**
 * Module summary:
 * This file creates and shares the browser-side Firebase SDK instances used by
 * the app. It cleans environment values, initializes Firebase once, and keeps
 * Auth persistence consistent so sessions survive refreshes and restarts.
 */

import { getApp as getExistingApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  getAuth,
  setPersistence,
  type Auth,
} from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';

// Client-side Firebase config — loaded from environment variables.
// Copy .env.example to .env.local and fill in your project values.
// Security is enforced by Firestore Security Rules, not by keeping these keys secret.

/** Strip surrounding quotes and whitespace that leak in from .env files. */
function cleanEnv(value: string | undefined): string {
  return (value || '').trim().replace(/^["']|["']$/g, '');
}

export const firebaseConfig = {
  // Clean each environment value before passing it to Firebase so accidental
  // quotes or spaces in .env files do not break SDK initialization.
  apiKey: cleanEnv(process.env.REACT_APP_FIREBASE_API_KEY),
  authDomain: cleanEnv(process.env.REACT_APP_FIREBASE_AUTH_DOMAIN),
  projectId: cleanEnv(process.env.REACT_APP_FIREBASE_PROJECT_ID),
  storageBucket: cleanEnv(process.env.REACT_APP_FIREBASE_STORAGE_BUCKET),
  messagingSenderId: cleanEnv(process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID),
  appId: cleanEnv(process.env.REACT_APP_FIREBASE_APP_ID),
};

if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.error(
    'Firebase configuration is missing. Copy .env.example to .env.local and fill in your project values.'
  );
}

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _authPersistence: Promise<Auth> | null = null;
let _db: Firestore | null = null;
let _storage: FirebaseStorage | null = null;

function getApp(): FirebaseApp {
  if (!_app) {
    // Reuse an already-initialized app when present so repeated imports do not
    // create duplicate Firebase app instances.
    _app = getApps().length ? getExistingApp() : initializeApp(firebaseConfig);
    console.info('[auth] Firebase app initialized');
  }
  return _app;
}

export function getFirebaseAuth(): Auth {
  if (!_auth) {
    // Create Auth lazily so pages that never authenticate do not initialize it unnecessarily.
    _auth = getAuth(getApp());
  }
  return _auth;
}

/**
 * Ensure Auth persistence is local so Firebase can remember the trusted
 * session/device across reloads and browser restarts.
 */
export function ensureFirebaseAuthPersistence(): Promise<Auth> {
  const auth = getFirebaseAuth();
  if (!_authPersistence) {
    console.info('[auth] Configuring Firebase auth persistence: browserLocalPersistence');
    // Tell Firebase Auth to keep the session in browser-local storage so the
    // user stays signed in across page reloads and browser restarts.
    _authPersistence = setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.info('[auth] Firebase auth persistence ready');
        return auth;
      })
      .catch((err) => {
        // Clear the cached promise after failure so the app can retry later.
        _authPersistence = null;
        throw err;
      });
  }
  return _authPersistence;
}

/**
 * Initialize Firebase Auth once at app startup so session restoration uses
 * the same Auth instance and persistence configuration everywhere.
 */
export function initializeFirebaseAuth(): Promise<Auth> {
  return ensureFirebaseAuthPersistence();
}

export function getFirebaseDb(): Firestore {
  if (!_db) {
    // Firestore is created only when data access is needed.
    _db = getFirestore(getApp());
  }
  return _db;
}

export function getFirebaseStorage(): FirebaseStorage {
  if (!_storage) {
    // Storage is created only when photo upload or download flows need it.
    _storage = getStorage(getApp());
  }
  return _storage;
}
